import React from "react";
import { createPortal } from "react-dom";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useTheme } from "../context/ThemeContext";
import { CASHOUT_STATUSES, TRANSACTION_TYPE, ORDER_EXECUTION_TYPE, ORDER_STATE_TYPE } from "../../Util/key_helper";
import { getPrimaryColor } from "../../Util/helper_functions";

export function getTruncatedText(text, maxLength = 20, className = "", tag = "div") {
	const isTruncated = text?.length > maxLength;
	const truncatedText = isTruncated ? `${text.substring(0, maxLength)}...` : text;
	const CustomTag = tag;
	return (
		<WithTooltip tooltipText={text} >
			<CustomTag
				className={` ${className}`}
				style={{
					whiteSpace: "wrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
					cursor: "pointer",
				}}
			>
				{truncatedText}
			</CustomTag>
		</WithTooltip>
	);
}


// Convert number into K (thousands), M (millions), B (billions), etc.
const formatCompact = (num, digits) => {
	num = Number(num); // Ensure num is a valid number
	const appliedDigits = digits !== undefined ? digits : 4;

	if (isNaN(num)) return ""; // Handle invalid numbers

	if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(appliedDigits) + "B"; // Billion
	if (num >= 1_000_000) return (num / 1_000_000).toFixed(appliedDigits) + "M"; // Million
	if (num >= 1_000) return (num / 1_000).toFixed(appliedDigits) + "K"; // Thousand

	return num % 1 !== 0 ? num?.toFixed(appliedDigits) : num?.toString(); // Show decimals if needed
};

export const compactNumberWithTooltip = (amount, title = "", digits) => {
	// Check if amount is a valid number
	if (isNaN(amount) || amount === "" || amount === null) {
		return <span></span>;  // Return an empty span if amount is invalid
	}
	const tooltipText = title ? `${title}: ${amount}` : `${amount}`;

	return (
		<WithTooltip tooltipText={tooltipText}>
			<span >
				{formatCompact(amount, digits)}
			</span>
		</WithTooltip>
	);
};

// Function to get status badge class
export const getStatusBadgeClassForCashout = (status) => {
	switch (status) {
		case CASHOUT_STATUSES.PENDING:
			return "warning-badge";
		case CASHOUT_STATUSES.INITIATED:
			return "info-badge";
		case CASHOUT_STATUSES.COMPLETED:
			return "success-badge";
		case CASHOUT_STATUSES.REJECTED:
			return "danger-badge";
		default:
			return "";
	}
};

//This wil return pnl with original value with color and icon
export function getPnlComponent(pnlVal) {
	if (pnlVal == null || undefined) {
		return "";
	}
	pnlVal = Number(parseFloat(pnlVal)?.toFixed(2));
	let colorClass;
	let icon;

	// Determine the color class and icon based on the value
	if (pnlVal < 0) {
		colorClass = 'danger-text ';
		icon = <i className="mdi mdi-arrow-down"></i>;
	} else if (pnlVal === 0.0 || pnlVal === null) {
		pnlVal = 0.0;
		icon = null; // No icon for zero or null values
	} else if (pnlVal > 0) {
		colorClass = 'success-text';
		icon = <i className="mdi mdi-arrow-up"></i>;
	}

	// Return the original pnlVal with color and icon, without formatting or tooltip
	return (
		<span className={colorClass}>
			{pnlVal}{icon}
		</span>
	);
}

export const useCommonSwitchProps = () => {
	const primaryColor = getPrimaryColor();
	return {
		offColor: "#808080",
		onColor: primaryColor,
		checkedIcon: false,
		uncheckedIcon: false,
		height: 15,
		width: 30
	};
};
export const useSmallSwitchProps = () => {
	const primaryColor = getPrimaryColor();
	return {
		offColor: "#808080",
		onColor: primaryColor,
		checkedIcon: false,
		uncheckedIcon: false,
		height: 12,
		width: 24
	}
};

// Touch devices have no hover, so the tooltip opens on tap there and on hover everywhere else
const isTouchDevice = () => typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches;

export const WithTooltip = ({ tooltipText, placement = 'top', children }) => {
	const tooltipId = React.useId();
	const openOnClick = isTouchDevice();
	return (
		<>
			<span data-tooltip-id={tooltipId} data-tooltip-content={tooltipText}>
				{children}
			</span>
			{createPortal(
				<ReactTooltip id={tooltipId} openOnClick={openOnClick} positionStrategy="fixed" place={placement} className="custom-tooltip" style={{ overflow: 'visible' }} />,
				document.body
			)}
		</>
	);
};
export const getOrderBadgeClassName = (type) =>
	[-1, 0, 2, 4, 6].includes(type) ? 'success' : 'danger';

export const getOrderStateBadgeClassName = (state) => {
	if ([4, 3].includes(state)) return 'success';    // FILLED, PARTIAL
	if ([2, 5, 6, -1].includes(state)) return 'danger';   // CANCELED, REJECTED, EXPIRED
	if ([0, 1, 7].includes(state)) return 'warning';  // STARTED, PLACED, REQUEST ADD
	return 'info'; // fallback
};

export const getTransactionTypeBadge = (type) => (
	<span className={`badge ${type === TRANSACTION_TYPE.CREDIT ? 'success' : 'danger'}-badge p-2`}>
		{type?.charAt(0).toUpperCase() + type?.slice(1)}
	</span>
);

export const getOrderTypeBadge = (type) => (
	<span className={`badge ${getOrderBadgeClassName(type)}-badge p-2`}>
		{ORDER_EXECUTION_TYPE[type]?.charAt(0).toUpperCase() + ORDER_EXECUTION_TYPE[type]?.slice(1)}
	</span>
);
export const getOrderStateBadge = (state, reason = '') => {
	const isFailed = +state === -1;
	const badge = (
		<span
			className={`badge ${getOrderStateBadgeClassName(state)}-badge p-2 ${isFailed ? 'cursor-pointer' : ''}`}
		>
			{ORDER_STATE_TYPE[state]?.charAt(0).toUpperCase() + ORDER_STATE_TYPE[state]?.slice(1)}
		</span>
	);

	return isFailed ? (
		<WithTooltip tooltipText={reason || 'Failed'}>
			{badge}
		</WithTooltip>
	) : (
		badge
	);
};



export const customSelectStyles = {

	control: (base, state) => ({
		...base,
		borderColor: state.isFocused ? 'var(--color-primary)' : 'var(--color-border)',
		'&:hover': {
			borderColor: 'var(--color-primary)',
		},
		boxShadow: state.isFocused
			? 'none' // still ok as is, optional to convert to a variable
			: 'none',
		minHeight: '32px',
		fontSize: '14px',
		backgroundColor: 'var( --surface-dropdown)',
		borderWidth: '1px',
		zIndex: 99,
		borderRadius: '5px',
		color: state.isFocused ? 'var(--text-on-primary)' : 'var(--color-text-primary)',
		opacity: state.isDisabled ? 0.5 : 1,
	}),
	input: (base) => ({
		...base,
		color: 'var(--color-text-primary)',
	}),
	option: (base, state) => ({
		...base,
		backgroundColor: state.isSelected
			? 'var(--color-primary)'
			: state.isFocused
				? 'var(--color-hover)'
				: 'var(--color-white)',
		color: state.isSelected || state.isFocused
			? 'var(--text-on-primary)'
			: 'var(--color-text-primary)',
		'&:active': {
			backgroundColor: 'var(--color-primary)',
			color: 'var(--text-on-primary)',
		},
		fontSize: '14px',
	}),
	placeholder: (base) => ({
		...base,
		color: 'var(--text-placeholder)',
		fontSize: '14px',
	}),
	singleValue: (base) => ({
		...base,
		color: 'var(--color-text-primary)',
	}),
	menu: (base) => ({
		...base,
		backgroundColor: 'var( --surface-dropdown)',
		border: '1px solid var(--color-border)',
		zIndex: 9999,
	}),
	menuList: (base) => ({
		...base,
		maxHeight: '180px',   // limit height
		overflowY: 'auto',    // enable vertical scroll
		paddingRight: '5px',  // avoid scrollbar overlapping text
	}),
};

